import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { 
  BookOpen, Plus, Edit, Trash2, BarChart3, Settings, 
  Users, FileQuestion, Trophy, Save, RefreshCw, Eye,
  CheckCircle, XCircle, Filter, Search, Upload, Sparkles,
  FileText, Download, Loader2, Wand2
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestSeriesAdmin = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [loading, setLoading] = useState(true);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  
  // Questions state
  const [questions, setQuestions] = useState([]);
  const [questionFilters, setQuestionFilters] = useState({ subject: '', topic: '', difficulty: '' });
  const [questionPage, setQuestionPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [filterOptions, setFilterOptions] = useState({ subjects: [], topics: [], difficulties: [] });
  
  // Tests state
  const [tests, setTests] = useState([]);
  
  // Plans state
  const [plans, setPlans] = useState([]);
  
  // Settings state
  const [settings, setSettings] = useState({
    show_solutions_after_test: true,
    show_correct_answers: true,
    allow_review: true,
    leaderboard_enabled: true,
    free_tests_limit: 5
  });
  
  // Bulk Import state
  const [importSettings, setImportSettings] = useState({
    ai_enabled: true,
    pdf_enabled: true,
    excel_enabled: true,
    ai_model: 'gemini-2.5-flash',
    ai_provider: 'gemini'
  });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [wordUploading, setWordUploading] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [aiForm, setAiForm] = useState({
    topic: '',
    subject: 'Physics',
    difficulty: 'medium',
    count: 10,
    include_solutions: true
  });
  const [pdfForm, setPdfForm] = useState({ subject: 'Physics', topic: '' });
  const [wordForm, setWordForm] = useState({ subject: 'Physics', topic: '' });
  const pdfInputRef = useRef(null);
  const wordInputRef = useRef(null);
  const csvInputRef = useRef(null);
  
  // Modal states
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingTest, setEditingTest] = useState(null);
  
  // Form states
  const [questionForm, setQuestionForm] = useState({
    subject: 'Physics',
    topic: '',
    subtopic: '',
    question: '',
    options: ['', '', '', ''],
    correct_option: 0,
    solution: '',
    difficulty: 'medium',
    marks: 4,
    negative_marks: 1
  });
  
  const [testForm, setTestForm] = useState({
    title: '',
    description: '',
    subject: 'All',
    type: 'topic_wise',
    duration_minutes: 30,
    total_questions: 15,
    total_marks: 60,
    is_free: true,
    pack: '',
    price: 0,
    difficulty: 'medium',
    topics: [],
    show_solutions: true
  });

  useEffect(() => {
    if (activeTab === 'analytics') fetchAnalytics();
    if (activeTab === 'questions') fetchQuestions();
    if (activeTab === 'tests') fetchTests();
    if (activeTab === 'plans') fetchPlans();
    if (activeTab === 'settings') fetchSettings();
    if (activeTab === 'bulk-import') fetchImportSettings();
  }, [activeTab, questionFilters, questionPage]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/test-series/analytics`);
      const data = await res.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({ title: 'Error', description: 'Failed to fetch analytics', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchImportSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/test-series/bulk-import/settings`);
      const data = await res.json();
      if (data.success) {
        setImportSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching import settings:', error);
    }
  };

  const saveImportSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/test-series/bulk-import/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(importSettings)
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: 'Import settings saved' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    }
  };

  const handleAiGenerate = async () => {
    if (!aiForm.topic) {
      toast({ title: 'Error', description: 'Please enter a topic', variant: 'destructive' });
      return;
    }
    
    setAiGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/test-series/bulk-import/ai-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(aiForm)
      });
      const data = await res.json();
      
      if (data.success) {
        toast({ title: 'Success', description: data.message });
        setAiForm({ ...aiForm, topic: '' });
        fetchQuestions();
        fetchAnalytics();
      } else {
        toast({ title: 'Error', description: data.error || 'AI generation failed', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'AI generation failed', variant: 'destructive' });
    } finally {
      setAiGenerating(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!pdfForm.topic) {
      toast({ title: 'Error', description: 'Please enter a topic first', variant: 'destructive' });
      return;
    }
    
    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subject', pdfForm.subject);
      formData.append('topic', pdfForm.topic);
      
      const res = await fetch(`${API_URL}/api/admin/test-series/bulk-import/pdf-import`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      
      if (data.success) {
        toast({ title: 'Success', description: data.message });
        fetchQuestions();
        fetchAnalytics();
      } else {
        toast({ title: 'Error', description: data.error || 'PDF import failed', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'PDF import failed', variant: 'destructive' });
    } finally {
      setPdfUploading(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const handleWordUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!wordForm.topic) {
      toast({ title: 'Error', description: 'Please enter a topic for the questions', variant: 'destructive' });
      return;
    }
    
    setWordUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subject', wordForm.subject);
      formData.append('topic', wordForm.topic);
      
      const res = await fetch(`${API_URL}/api/admin/test-series/bulk-import/word-import`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      
      if (data.success) {
        toast({ title: 'Success', description: data.message });
        fetchQuestions();
        fetchAnalytics();
      } else {
        toast({ title: 'Error', description: data.error || 'Word import failed', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Word import failed', variant: 'destructive' });
    } finally {
      setWordUploading(false);
      if (wordInputRef.current) wordInputRef.current.value = '';
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/admin/test-series/bulk-import/excel-import`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      
      if (data.success) {
        toast({ title: 'Success', description: data.message });
        if (data.errors?.length > 0) {
          console.log('Import errors:', data.errors);
        }
        fetchQuestions();
        fetchAnalytics();
      } else {
        toast({ title: 'Error', description: data.error || 'CSV import failed', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'CSV import failed', variant: 'destructive' });
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const downloadCsvTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/test-series/bulk-import/excel-template`);
      const data = await res.json();
      
      if (data.success) {
        const blob = new Blob([data.template], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'question_import_template.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download template', variant: 'destructive' });
    }
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: questionPage, limit: 20 });
      if (questionFilters.subject) params.append('subject', questionFilters.subject);
      if (questionFilters.topic) params.append('topic', questionFilters.topic);
      if (questionFilters.difficulty) params.append('difficulty', questionFilters.difficulty);
      
      const res = await fetch(`${API_URL}/api/admin/test-series/questions?${params}`);
      const data = await res.json();
      if (data.success) {
        setQuestions(data.questions);
        setTotalQuestions(data.total);
        setFilterOptions(data.filter_options || { subjects: [], topics: [], difficulties: [] });
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTests = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/test-series/tests`);
      const data = await res.json();
      if (data.success) {
        setTests(data.tests);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/test-series/plans`);
      const data = await res.json();
      if (data.success) {
        setPlans(data.plans);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/admin/test-series/settings`);
      const data = await res.json();
      if (data.success) {
        setSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuestion = async () => {
    try {
      const url = editingQuestion 
        ? `${API_URL}/api/admin/test-series/questions/${editingQuestion.id}`
        : `${API_URL}/api/admin/test-series/questions`;
      
      const res = await fetch(url, {
        method: editingQuestion ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(questionForm)
      });
      
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: editingQuestion ? 'Question updated' : 'Question created' });
        setShowQuestionModal(false);
        setEditingQuestion(null);
        resetQuestionForm();
        fetchQuestions();
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to save question', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Error saving question:', error);
      toast({ title: 'Error', description: 'Failed to save question', variant: 'destructive' });
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!confirm('Are you sure you want to delete this question?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/test-series/questions/${questionId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: 'Question deleted' });
        fetchQuestions();
      }
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleSaveTest = async () => {
    try {
      const url = editingTest 
        ? `${API_URL}/api/admin/test-series/tests/${editingTest.id}`
        : `${API_URL}/api/admin/test-series/tests`;
      
      const res = await fetch(url, {
        method: editingTest ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testForm)
      });
      
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: editingTest ? 'Test updated' : 'Test created' });
        setShowTestModal(false);
        setEditingTest(null);
        resetTestForm();
        fetchTests();
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to save test', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Error saving test:', error);
      toast({ title: 'Error', description: 'Failed to save test', variant: 'destructive' });
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!confirm('Are you sure you want to delete this test?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/test-series/tests/${testId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: 'Test deleted' });
        fetchTests();
      }
    } catch (error) {
      console.error('Error deleting test:', error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/test-series/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Success', description: 'Settings saved' });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    }
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      subject: 'Physics',
      topic: '',
      subtopic: '',
      question: '',
      options: ['', '', '', ''],
      correct_option: 0,
      solution: '',
      difficulty: 'medium',
      marks: 4,
      negative_marks: 1
    });
  };

  const resetTestForm = () => {
    setTestForm({
      title: '',
      description: '',
      subject: 'All',
      type: 'topic_wise',
      duration_minutes: 30,
      total_questions: 15,
      total_marks: 60,
      is_free: true,
      pack: '',
      price: 0,
      difficulty: 'medium',
      topics: [],
      show_solutions: true
    });
  };

  const openEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      subject: question.subject,
      topic: question.topic,
      subtopic: question.subtopic || '',
      question: question.question,
      options: question.options,
      correct_option: question.correct_option,
      solution: question.solution,
      difficulty: question.difficulty,
      marks: question.marks || 4,
      negative_marks: question.negative_marks || 1
    });
    setShowQuestionModal(true);
  };

  const openEditTest = (test) => {
    setEditingTest(test);
    setTestForm({
      title: test.title,
      description: test.description,
      subject: test.subject,
      type: test.type,
      duration_minutes: test.duration_minutes,
      total_questions: test.total_questions,
      total_marks: test.total_marks,
      is_free: test.is_free,
      pack: test.pack || '',
      price: test.price || 0,
      difficulty: test.difficulty,
      topics: test.topics || [],
      show_solutions: test.show_solutions !== false
    });
    setShowTestModal(true);
  };

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'questions', label: 'Questions', icon: FileQuestion },
    { id: 'bulk-import', label: 'Bulk Import', icon: Upload },
    { id: 'tests', label: 'Tests', icon: BookOpen },
    { id: 'plans', label: 'Plans', icon: Trophy },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Test Series Management</h2>
        <Button onClick={() => activeTab === 'questions' ? fetchQuestions() : activeTab === 'tests' ? fetchTests() : fetchAnalytics()} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            className="gap-2"
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Total Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">{analytics.total_questions}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Total Tests</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">{analytics.total_tests}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-purple-600">{analytics.total_users}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-600">Test Attempts (7d)</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-orange-600">{analytics.recent_attempts_7d}</p>
            </CardContent>
          </Card>
          
          {/* Subject Distribution */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Questions by Subject</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(analytics.subject_distribution || {}).map(([subject, count]) => (
                  <div key={subject} className="flex items-center justify-between">
                    <span className="text-slate-600">{subject}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${(count / analytics.total_questions) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Difficulty Distribution */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Questions by Difficulty</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                {Object.entries(analytics.difficulty_distribution || {}).map(([diff, count]) => (
                  <div key={diff} className="flex-1 text-center p-4 bg-slate-50 rounded-lg">
                    <Badge variant={diff === 'easy' ? 'secondary' : diff === 'medium' ? 'default' : 'destructive'}>
                      {diff}
                    </Badge>
                    <p className="text-2xl font-bold mt-2">{count}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Questions Tab */}
      {activeTab === 'questions' && (
        <div className="space-y-4">
          {/* Filters and Add Button */}
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-2">
              <Select value={questionFilters.subject || 'all'} onValueChange={(v) => setQuestionFilters({ ...questionFilters, subject: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  {filterOptions.subjects?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={questionFilters.difficulty || 'all'} onValueChange={(v) => setQuestionFilters({ ...questionFilters, difficulty: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Difficulty" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="easy">Easy</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="hard">Hard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { resetQuestionForm(); setEditingQuestion(null); setShowQuestionModal(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Add Question
            </Button>
          </div>

          {/* Questions List */}
          <div className="space-y-3">
            {questions.map((q, idx) => (
              <Card key={q.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex gap-2 mb-2">
                        <Badge variant="outline">{q.subject}</Badge>
                        <Badge variant="secondary">{q.topic}</Badge>
                        <Badge variant={q.difficulty === 'easy' ? 'secondary' : q.difficulty === 'hard' ? 'destructive' : 'default'}>
                          {q.difficulty}
                        </Badge>
                      </div>
                      <p className="text-slate-800 font-medium">{q.question}</p>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                        {q.options?.map((opt, i) => (
                          <div key={i} className={`p-2 rounded ${i === q.correct_option ? 'bg-green-100 text-green-800' : 'bg-slate-50'}`}>
                            {String.fromCharCode(65 + i)}. {opt}
                            {i === q.correct_option && <CheckCircle className="inline w-4 h-4 ml-1" />}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditQuestion(q)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteQuestion(q.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">Showing {questions.length} of {totalQuestions} questions</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={questionPage === 1} onClick={() => setQuestionPage(p => p - 1)}>
                Previous
              </Button>
              <Button size="sm" variant="outline" disabled={questions.length < 20} onClick={() => setQuestionPage(p => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Tab */}
      {activeTab === 'bulk-import' && (
        <div className="space-y-6">
          {/* Import Method Toggles */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" /> Import Method Settings
              </CardTitle>
              <CardDescription>Enable or disable each import method. AI generation uses Emergent LLM credits.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  <div>
                    <Label className="font-medium">AI Question Generator</Label>
                    <p className="text-sm text-slate-500">Generate questions using AI (~$0.01 per 10 questions)</p>
                  </div>
                </div>
                <Switch 
                  checked={importSettings.ai_enabled}
                  onCheckedChange={(v) => setImportSettings({ ...importSettings, ai_enabled: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-red-500" />
                  <div>
                    <Label className="font-medium">PDF Import</Label>
                    <p className="text-sm text-slate-500">Extract questions from PDF documents using AI</p>
                  </div>
                </div>
                <Switch 
                  checked={importSettings.pdf_enabled}
                  onCheckedChange={(v) => setImportSettings({ ...importSettings, pdf_enabled: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Download className="w-5 h-5 text-green-500" />
                  <div>
                    <Label className="font-medium">Excel/CSV Import</Label>
                    <p className="text-sm text-slate-500">Bulk import from spreadsheet (free, no AI cost)</p>
                  </div>
                </div>
                <Switch 
                  checked={importSettings.excel_enabled}
                  onCheckedChange={(v) => setImportSettings({ ...importSettings, excel_enabled: v })}
                />
              </div>
              {importSettings.ai_enabled && (
                <div className="pt-2 border-t">
                  <Label className="text-sm">AI Model (for AI & PDF import)</Label>
                  <Select value={importSettings.ai_model} onValueChange={(v) => setImportSettings({ ...importSettings, ai_model: v })}>
                    <SelectTrigger className="w-64 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gemini-2.5-flash">Gemini Flash (Cheapest)</SelectItem>
                      <SelectItem value="gemini-2.5-pro">Gemini Pro</SelectItem>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem>
                      <SelectItem value="gpt-4o">GPT-4o</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button onClick={saveImportSettings} className="mt-2">
                <Save className="w-4 h-4 mr-2" /> Save Settings
              </Button>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-3 gap-6">
            {/* AI Generation Card */}
            <Card className={!importSettings.ai_enabled ? 'opacity-50 pointer-events-none' : ''}>
              <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5" /> AI Question Generator
                </CardTitle>
                <CardDescription className="text-white/80">Generate JEE questions instantly</CardDescription>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div>
                  <Label>Topic</Label>
                  <Input 
                    placeholder="e.g., Projectile Motion, Organic Chemistry Reactions"
                    value={aiForm.topic}
                    onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Subject</Label>
                    <Select value={aiForm.subject} onValueChange={(v) => setAiForm({ ...aiForm, subject: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Physics">Physics</SelectItem>
                        <SelectItem value="Chemistry">Chemistry</SelectItem>
                        <SelectItem value="Mathematics">Mathematics</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Difficulty</Label>
                    <Select value={aiForm.difficulty} onValueChange={(v) => setAiForm({ ...aiForm, difficulty: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Number of Questions</Label>
                  <Input 
                    type="number"
                    min="1"
                    max="20"
                    value={aiForm.count}
                    onChange={(e) => setAiForm({ ...aiForm, count: parseInt(e.target.value) })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={aiForm.include_solutions}
                    onCheckedChange={(v) => setAiForm({ ...aiForm, include_solutions: v })}
                  />
                  <Label>Include detailed solutions</Label>
                </div>
                <Button onClick={handleAiGenerate} disabled={aiGenerating} className="w-full">
                  {aiGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" /> Generate Questions
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* PDF Import Card */}
            <Card className={!importSettings.pdf_enabled ? 'opacity-50 pointer-events-none' : ''}>
              <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" /> PDF Import
                </CardTitle>
                <CardDescription className="text-white/80">Extract questions from PDF files</CardDescription>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div>
                  <Label>Subject</Label>
                  <Select value={pdfForm.subject} onValueChange={(v) => setPdfForm({ ...pdfForm, subject: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Physics">Physics</SelectItem>
                      <SelectItem value="Chemistry">Chemistry</SelectItem>
                      <SelectItem value="Mathematics">Mathematics</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Topic</Label>
                  <Input 
                    placeholder="e.g., JEE 2023 PYQ"
                    value={pdfForm.topic}
                    onChange={(e) => setPdfForm({ ...pdfForm, topic: e.target.value })}
                  />
                </div>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 mb-2">Upload PDF with questions</p>
                  <input 
                    type="file"
                    accept=".pdf"
                    ref={pdfInputRef}
                    onChange={handlePdfUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => pdfInputRef.current?.click()}
                    disabled={pdfUploading}
                  >
                    {pdfUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...
                      </>
                    ) : (
                      'Select PDF'
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Supports: JEE papers, coaching material, question banks
                </p>
              </CardContent>
            </Card>

            {/* Word Import Card */}
            <Card className={!importSettings.pdf_enabled ? 'opacity-50 pointer-events-none' : ''}>
              <CardHeader className="bg-gradient-to-r from-indigo-500 to-blue-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Word Import
                </CardTitle>
                <CardDescription className="text-white/80">Extract questions from .docx files</CardDescription>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Select value={wordForm.subject} onValueChange={(v) => setWordForm({ ...wordForm, subject: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Physics', 'Chemistry', 'Mathematics'].map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input 
                    placeholder="Topic"
                    value={wordForm.topic}
                    onChange={(e) => setWordForm({ ...wordForm, topic: e.target.value })}
                  />
                </div>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 mb-2">Upload Word document with questions</p>
                  <input 
                    type="file"
                    accept=".docx"
                    ref={wordInputRef}
                    onChange={handleWordUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => wordInputRef.current?.click()}
                    disabled={wordUploading}
                  >
                    {wordUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting...
                      </>
                    ) : (
                      'Select Word File'
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Supports: .docx files with questions (AI will extract MCQs)
                </p>
              </CardContent>
            </Card>

            {/* CSV Import Card */}
            <Card className={!importSettings.excel_enabled ? 'opacity-50 pointer-events-none' : ''}>
              <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" /> Excel/CSV Import
                </CardTitle>
                <CardDescription className="text-white/80">Bulk import from spreadsheet</CardDescription>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-sm text-green-800 font-medium">Free Method - No AI Cost!</p>
                  <p className="text-xs text-green-600">Download template, fill in questions, upload</p>
                </div>
                <Button variant="outline" onClick={downloadCsvTemplate} className="w-full">
                  <Download className="w-4 h-4 mr-2" /> Download Template
                </Button>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600 mb-2">Upload filled CSV</p>
                  <input 
                    type="file"
                    accept=".csv"
                    ref={csvInputRef}
                    onChange={handleCsvUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => csvInputRef.current?.click()}
                    disabled={csvUploading}
                  >
                    {csvUploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...
                      </>
                    ) : (
                      'Select CSV'
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Format: Subject, Topic, Question, Options A-D, Answer, Solution
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Tests Tab */}
      {activeTab === 'tests' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { resetTestForm(); setEditingTest(null); setShowTestModal(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Add Test
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tests.map(test => (
              <Card key={test.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{test.title}</CardTitle>
                      <CardDescription>{test.description}</CardDescription>
                    </div>
                    <Badge variant={test.is_free ? 'secondary' : 'default'}>
                      {test.is_free ? 'Free' : `₹${test.price}`}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p>Subject: <strong>{test.subject}</strong></p>
                    <p>Questions: <strong>{test.total_questions}</strong> | Duration: <strong>{test.duration_minutes} min</strong></p>
                    <p>Attempts: <strong>{test.attempt_count || 0}</strong></p>
                    <div className="flex items-center gap-2 pt-2">
                      <Badge variant={test.show_solutions !== false ? 'secondary' : 'outline'}>
                        {test.show_solutions !== false ? 'Solutions ON' : 'Solutions OFF'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditTest(test)}>
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteTest(test.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(plan => (
            <Card key={plan.id} className={`${plan.id === 'premium' ? 'border-2 border-purple-500' : ''}`}>
              <CardHeader>
                <CardTitle className="flex justify-between items-center">
                  {plan.name}
                  <Badge>{plan.subscriber_count || 0} users</Badge>
                </CardTitle>
                <CardDescription>
                  {plan.price === 0 ? 'Free' : `₹${plan.price}`}
                  {plan.original_price && <span className="line-through text-slate-400 ml-2">₹{plan.original_price}</span>}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {plan.features?.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-slate-500 mt-4">Duration: {plan.duration_days} days</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card>
          <CardHeader>
            <CardTitle>Test Series Settings</CardTitle>
            <CardDescription>Configure global settings for the test series</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Show Solutions After Test</Label>
                <p className="text-sm text-slate-500">Display detailed solutions after test submission</p>
              </div>
              <Switch 
                checked={settings.show_solutions_after_test}
                onCheckedChange={(v) => setSettings({ ...settings, show_solutions_after_test: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Show Correct Answers</Label>
                <p className="text-sm text-slate-500">Show which answers were correct</p>
              </div>
              <Switch 
                checked={settings.show_correct_answers}
                onCheckedChange={(v) => setSettings({ ...settings, show_correct_answers: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Allow Review</Label>
                <p className="text-sm text-slate-500">Allow users to review their answers</p>
              </div>
              <Switch 
                checked={settings.allow_review}
                onCheckedChange={(v) => setSettings({ ...settings, allow_review: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Leaderboard Enabled</Label>
                <p className="text-sm text-slate-500">Show test leaderboards</p>
              </div>
              <Switch 
                checked={settings.leaderboard_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, leaderboard_enabled: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="font-medium">Free Tests Limit</Label>
                <p className="text-sm text-slate-500">Number of free tests per user</p>
              </div>
              <Input 
                type="number"
                className="w-20"
                value={settings.free_tests_limit}
                onChange={(e) => setSettings({ ...settings, free_tests_limit: parseInt(e.target.value) })}
              />
            </div>
            <Button onClick={handleSaveSettings} className="w-full">
              <Save className="w-4 h-4 mr-2" /> Save Settings
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Question Modal */}
      <Dialog open={showQuestionModal} onOpenChange={setShowQuestionModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add New Question'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Subject</Label>
                <Select value={questionForm.subject} onValueChange={(v) => setQuestionForm({ ...questionForm, subject: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Physics">Physics</SelectItem>
                    <SelectItem value="Chemistry">Chemistry</SelectItem>
                    <SelectItem value="Mathematics">Mathematics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Topic</Label>
                <Input value={questionForm.topic} onChange={(e) => setQuestionForm({ ...questionForm, topic: e.target.value })} placeholder="e.g., mechanics" />
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={questionForm.difficulty} onValueChange={(v) => setQuestionForm({ ...questionForm, difficulty: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Question</Label>
              <Textarea 
                value={questionForm.question} 
                onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                placeholder="Enter the question..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Options (select correct answer)</Label>
              {questionForm.options.map((opt, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input 
                    type="radio" 
                    name="correct" 
                    checked={questionForm.correct_option === i}
                    onChange={() => setQuestionForm({ ...questionForm, correct_option: i })}
                  />
                  <span className="w-6">{String.fromCharCode(65 + i)}.</span>
                  <Input 
                    value={opt}
                    onChange={(e) => {
                      const newOpts = [...questionForm.options];
                      newOpts[i] = e.target.value;
                      setQuestionForm({ ...questionForm, options: newOpts });
                    }}
                    placeholder={`Option ${String.fromCharCode(65 + i)}`}
                  />
                </div>
              ))}
            </div>

            <div>
              <Label>Solution</Label>
              <Textarea 
                value={questionForm.solution}
                onChange={(e) => setQuestionForm({ ...questionForm, solution: e.target.value })}
                placeholder="Enter the detailed solution..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Marks</Label>
                <Input type="number" value={questionForm.marks} onChange={(e) => setQuestionForm({ ...questionForm, marks: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Negative Marks</Label>
                <Input type="number" value={questionForm.negative_marks} onChange={(e) => setQuestionForm({ ...questionForm, negative_marks: parseInt(e.target.value) })} />
              </div>
            </div>

            <Button onClick={handleSaveQuestion} className="w-full">
              <Save className="w-4 h-4 mr-2" /> {editingQuestion ? 'Update Question' : 'Create Question'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTest ? 'Edit Test' : 'Add New Test'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={testForm.title} onChange={(e) => setTestForm({ ...testForm, title: e.target.value })} placeholder="Test title" />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea value={testForm.description} onChange={(e) => setTestForm({ ...testForm, description: e.target.value })} placeholder="Test description" />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Subject</Label>
                <Select value={testForm.subject} onValueChange={(v) => setTestForm({ ...testForm, subject: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Subjects</SelectItem>
                    <SelectItem value="Physics">Physics</SelectItem>
                    <SelectItem value="Chemistry">Chemistry</SelectItem>
                    <SelectItem value="Mathematics">Mathematics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={testForm.type} onValueChange={(v) => setTestForm({ ...testForm, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="topic_wise">Topic Wise</SelectItem>
                    <SelectItem value="mini_mock">Mini Mock</SelectItem>
                    <SelectItem value="full_mock">Full Mock</SelectItem>
                    <SelectItem value="pyq">PYQ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulty</Label>
                <Select value={testForm.difficulty} onValueChange={(v) => setTestForm({ ...testForm, difficulty: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" value={testForm.duration_minutes} onChange={(e) => setTestForm({ ...testForm, duration_minutes: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Total Questions</Label>
                <Input type="number" value={testForm.total_questions} onChange={(e) => setTestForm({ ...testForm, total_questions: parseInt(e.target.value) })} />
              </div>
              <div>
                <Label>Total Marks</Label>
                <Input type="number" value={testForm.total_marks} onChange={(e) => setTestForm({ ...testForm, total_marks: parseInt(e.target.value) })} />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={testForm.is_free} onCheckedChange={(v) => setTestForm({ ...testForm, is_free: v })} />
                <Label>Free Test</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={testForm.show_solutions} onCheckedChange={(v) => setTestForm({ ...testForm, show_solutions: v })} />
                <Label>Show Solutions</Label>
              </div>
            </div>

            {!testForm.is_free && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pack</Label>
                  <Select value={testForm.pack || 'basic'} onValueChange={(v) => setTestForm({ ...testForm, pack: v })}>
                    <SelectTrigger><SelectValue placeholder="Select pack" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Price (₹)</Label>
                  <Input type="number" value={testForm.price} onChange={(e) => setTestForm({ ...testForm, price: parseInt(e.target.value) })} />
                </div>
              </div>
            )}

            <Button onClick={handleSaveTest} className="w-full">
              <Save className="w-4 h-4 mr-2" /> {editingTest ? 'Update Test' : 'Create Test'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TestSeriesAdmin;
